import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Switch, FormControlLabel, useTheme, useMediaQuery, styled, alpha } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { MagicStar } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { useForm, Controller } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import { HFilledTextField } from '@ui/components/common/TextField';
import { Icon } from '@iconify/react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllEmailPreferencesAsync, createEmailPreferenceAsync, updateEmailPreferenceAsync } from '@application/features/emailPreferences/emailPreferencesThunk';
import { EmailType } from '@application/features/emailPreferences/models/email-type-enum';
import { Language } from '@application/features/emailPreferences/models/language-enum';
import { APIStatus } from '@application/enums/api-status';
// Styled Components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
    boxShadow: 'none',
    border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
    borderRadius: '14px !important',
    overflow: 'hidden',
    '&:before': {
        display: 'none'
    },
    '&.Mui-expanded': {
        margin: 0
    },
    '&.MuiPaper-root-MuiAccordion-root': {
        borderRadius: '14px !important'
    }
}));
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: '14px',
    padding: theme.spacing(1.5, 2.5),
    '&.Mui-expanded': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        paddingBottom: 0
    },
    '& .MuiAccordionSummary-content': {
        alignItems: 'center',
        margin: 0
    }
}));
const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 2.5)
}));
const ItemContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    backgroundColor: theme.palette.common.grey8,
    borderRadius: '14px',
    padding: theme.spacing(1.5),
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between'
}));
export const EmailControlTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
    const isMobileScreen = useMediaQuery(theme.breakpoints.down(1024));
    const [expanded, setExpanded] = useState(['emailControl']);
    // Get email preferences from Redux store
    const emailPreferences = useAppSelector((state) => state.emailPreferences.emailPreferences);
    const emailPreferencesStatus = useAppSelector((state) => state.emailPreferences.status);
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    // Check if user has spheremaster/sfeermeester role
    const isSpheremaster = useMemo(() => {
        if (!currentUser?.role)
            return false;
        const roles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role];
        return roles.some((role) => role.toLowerCase().includes('spheremaster') ||
            role.toLowerCase().includes('sfeermeester') ||
            role === 'Sfeermeester');
    }, [currentUser]);
    // Load email preferences on mount
    useEffect(() => {
        dispatch(getAllEmailPreferencesAsync());
    }, [dispatch]);
    // Map email preferences to UI state
    const preferencesMap = useMemo(() => {
        const map = {
            [EmailType.SfeermasterActionList]: emailPreferences.find((ep) => ep.emailType === EmailType.SfeermasterActionList),
            [EmailType.JubileesMonthlyOverview]: emailPreferences.find((ep) => ep.emailType === EmailType.JubileesMonthlyOverview),
            [EmailType.EventReminder]: emailPreferences.find((ep) => ep.emailType === EmailType.EventReminder)
        };
        return map;
    }, [emailPreferences]);
    // Calculate global toggle state (at least one enabled)
    const emailControlEnabled = useMemo(() => {
        return emailPreferences.length > 0 && emailPreferences.some((ep) => ep.isEnabled);
    }, [emailPreferences]);
    // Get enabled states for individual email types
    const enabledStates = useMemo(() => ({
        sfeermaster: preferencesMap[EmailType.SfeermasterActionList]?.isEnabled ?? false,
        jubilees: preferencesMap[EmailType.JubileesMonthlyOverview]?.isEnabled ?? false,
        eventReminder: preferencesMap[EmailType.EventReminder]?.isEnabled ?? false
    }), [preferencesMap]);
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? [...expanded, panel] : expanded.filter((p) => p !== panel));
    };
    // Get current timing settings values for form
    const getTimingValue = (emailType) => {
        const pref = preferencesMap[emailType];
        if (!pref || !pref.timingSettings || pref.timingSettings.length === 0) {
            return emailType === EmailType.EventReminder ? [] : '';
        }
        const timing = pref.timingSettings[0];
        if (emailType === EmailType.SfeermasterActionList &&
            timing.daysOfWeek &&
            timing.daysOfWeek.length > 0) {
            return String(timing.daysOfWeek[0]);
        }
        if (emailType === EmailType.JubileesMonthlyOverview &&
            timing.daysOfMonth &&
            timing.daysOfMonth.length > 0) {
            return String(timing.daysOfMonth[0]);
        }
        if (emailType === EmailType.EventReminder &&
            timing.daysBefore &&
            timing.daysBefore.length > 0) {
            return timing.daysBefore.map((d) => String(d));
        }
        return emailType === EmailType.EventReminder ? [] : '';
    };
    // Get current language value for form
    const getLanguageValue = (emailType) => {
        const pref = preferencesMap[emailType];
        if (!pref) {
            return String(Language.EN); // Default to English
        }
        return String(pref.language);
    };
    // Language options with flags
    const languageOptions = useMemo(() => [
        {
            value: String(Language.NL),
            label: 'Nederlands',
            flag: '/assets/images/flags/nl-flag.svg'
        },
        {
            value: String(Language.EN),
            label: 'English',
            flag: '/assets/images/flags/us-flag.svg'
        },
        {
            value: String(Language.DE),
            label: 'Deutsch',
            flag: '/assets/images/flags/de-flag.svg'
        }
    ], []);
    // Email control data with real options - memoized with t dependency for translations
    const emailControlData = useMemo(() => {
        // Days of week options - translated
        const daysOfWeekOptions = [
            { value: '0', label: t('common.sunday') },
            { value: '1', label: t('common.monday') },
            { value: '2', label: t('common.tuesday') },
            { value: '3', label: t('common.wednesday') },
            { value: '4', label: t('common.thursday') },
            { value: '5', label: t('common.friday') },
            { value: '6', label: t('common.saturday') }
        ];
        // Days of month options (limited to 28 days - all months have at least 28 days)
        const daysOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
            value: String(i + 1),
            label: String(i + 1)
        }));
        // Days before options - translated
        const daysBeforeOptions = [
            { value: '1', label: t('settings.reminder1Day') },
            { value: '3', label: t('settings.reminder3Days') },
            { value: '7', label: t('settings.reminder7Days') }
        ];
        const allOptions = [
            {
                id: 'sfeermaster',
                emailType: EmailType.SfeermasterActionList,
                label: t('settings.emailControl.sfeermaster'),
                description: t('settings.emailControl.sfeermasterDescription'),
                options: daysOfWeekOptions,
                defaultOption: getTimingValue(EmailType.SfeermasterActionList) || '1',
                dropdownLabel: t('settings.emailControl.daysOfWeek')
            },
            {
                id: 'jubilees',
                emailType: EmailType.JubileesMonthlyOverview,
                label: t('settings.emailControl.jubilees'),
                description: t('settings.emailControl.jubileesDescription'),
                options: daysOfMonthOptions,
                defaultOption: getTimingValue(EmailType.JubileesMonthlyOverview) || '1',
                dropdownLabel: t('settings.emailControl.dayOfMonth')
            },
            {
                id: 'eventReminder',
                emailType: EmailType.EventReminder,
                label: t('settings.emailControl.eventReminder'),
                description: t('settings.emailControl.eventReminderDescription'),
                options: daysBeforeOptions,
                defaultOption: getTimingValue(EmailType.EventReminder) || ['3'],
                dropdownLabel: t('settings.emailControl.remindBefore'),
                isMultiple: true
            }
        ];
        // Filter options based on user role
        if (isSpheremaster) {
            return allOptions.filter((option) => option.id === 'eventReminder');
        }
        return allOptions;
    }, [t, preferencesMap, isSpheremaster]);
    const defaultValues = useMemo(() => emailControlData.reduce((acc, item) => ({
        ...acc,
        [`dropdown-${item.id}`]: item.defaultOption,
        [`language-${item.id}`]: getLanguageValue(item.emailType)
    }), {}), [emailControlData, preferencesMap]);
    const { control, watch, reset } = useForm({
        defaultValues
    });
    // Watch form values for changes
    const formValues = watch();
    const isInitializingRef = React.useRef(true);
    // Handle dropdown change
    const handleDropdownChange = useCallback(async (id, value) => {
        const item = emailControlData.find((d) => d.id === id);
        if (!item)
            return;
        const existingPref = preferencesMap[item.emailType];
        if (!existingPref)
            return;
        const currentLanguage = formValues?.[`language-${id}`] || String(existingPref.language);
        let updatedTimingSettings = [];
        if (item.emailType === EmailType.SfeermasterActionList) {
            updatedTimingSettings = [
                {
                    daysOfWeek: [Number(value)],
                    orderIndex: 0
                }
            ];
        }
        else if (item.emailType === EmailType.JubileesMonthlyOverview) {
            updatedTimingSettings = [
                {
                    daysOfMonth: [Number(value)],
                    orderIndex: 0
                }
            ];
        }
        else if (item.emailType === EmailType.EventReminder) {
            const existingTiming = existingPref.timingSettings[0];
            const daysBeforeArray = Array.isArray(value)
                ? value.map((v) => Number(v))
                : [Number(value)];
            updatedTimingSettings = [
                {
                    daysBefore: daysBeforeArray,
                    isDayItself: existingTiming?.isDayItself ?? false,
                    orderIndex: 0
                }
            ];
        }
        const updateRequest = {
            id: existingPref.id,
            emailType: existingPref.emailType,
            isEnabled: existingPref.isEnabled,
            language: Number(currentLanguage),
            timingSettings: updatedTimingSettings
        };
        await dispatch(updateEmailPreferenceAsync(updateRequest));
    }, [emailControlData, preferencesMap, dispatch, formValues]);
    // Handle language change
    const handleLanguageChange = useCallback(async (id, value) => {
        const item = emailControlData.find((d) => d.id === id);
        if (!item)
            return;
        const existingPref = preferencesMap[item.emailType];
        if (!existingPref)
            return;
        const updateRequest = {
            id: existingPref.id,
            emailType: existingPref.emailType,
            isEnabled: existingPref.isEnabled,
            language: Number(value),
            timingSettings: existingPref.timingSettings.map((ts) => ({
                daysOfWeek: ts.daysOfWeek,
                daysOfMonth: ts.daysOfMonth,
                daysBefore: ts.daysBefore,
                isDayItself: ts.isDayItself,
                orderIndex: ts.orderIndex
            }))
        };
        await dispatch(updateEmailPreferenceAsync(updateRequest));
    }, [emailControlData, preferencesMap, dispatch]);
    // Reset form when preferences load
    useEffect(() => {
        if (emailPreferences.length > 0) {
            isInitializingRef.current = true;
            const newDefaults = emailControlData.reduce((acc, item) => ({
                ...acc,
                [`dropdown-${item.id}`]: item.defaultOption,
                [`language-${item.id}`]: getLanguageValue(item.emailType)
            }), {});
            reset(newDefaults);
            // Allow updates after a short delay
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 1000);
        }
    }, [emailPreferences, emailControlData, reset]);
    // Watch for dropdown changes and update backend (only after initialization)
    useEffect(() => {
        if (!formValues || emailPreferences.length === 0 || isInitializingRef.current)
            return;
        const timers = [];
        emailControlData.forEach((item) => {
            const fieldName = `dropdown-${item.id}`;
            const languageFieldName = `language-${item.id}`;
            const currentValue = formValues[fieldName];
            const currentLanguage = formValues[languageFieldName];
            const existingPref = preferencesMap[item.emailType];
            if (existingPref && currentValue) {
                // Get current timing value from backend
                const timing = existingPref.timingSettings?.[0];
                let currentTimingValue = '';
                let hasChanged = false;
                if (item.emailType === EmailType.SfeermasterActionList &&
                    timing?.daysOfWeek?.[0] !== undefined) {
                    currentTimingValue = String(timing.daysOfWeek[0]);
                    hasChanged = currentValue !== currentTimingValue;
                }
                else if (item.emailType === EmailType.JubileesMonthlyOverview &&
                    timing?.daysOfMonth?.[0] !== undefined) {
                    currentTimingValue = String(timing.daysOfMonth[0]);
                    hasChanged = currentValue !== currentTimingValue;
                }
                else if (item.emailType === EmailType.EventReminder && timing?.daysBefore) {
                    currentTimingValue = timing.daysBefore.map((d) => String(d)).sort();
                    const currentValueArray = Array.isArray(currentValue)
                        ? [...currentValue].sort()
                        : [String(currentValue)];
                    hasChanged = JSON.stringify(currentValueArray) !== JSON.stringify(currentTimingValue);
                }
                // Only update if value is different from backend
                if (hasChanged) {
                    const timer = setTimeout(() => {
                        const valueToSend = item.emailType === EmailType.EventReminder
                            ? Array.isArray(currentValue)
                                ? currentValue
                                : [currentValue]
                            : currentValue;
                        handleDropdownChange(item.id, valueToSend);
                    }, 800);
                    timers.push(timer);
                }
            }
            // Watch for language changes
            if (existingPref && currentLanguage) {
                const currentLanguageValue = String(existingPref.language);
                const languageValue = typeof currentLanguage === 'string' ? currentLanguage : String(currentLanguage);
                if (languageValue !== currentLanguageValue) {
                    const timer = setTimeout(() => {
                        handleLanguageChange(item.id, languageValue);
                    }, 800);
                    timers.push(timer);
                }
            }
        });
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
        };
    }, [
        formValues,
        emailPreferences,
        emailControlData,
        preferencesMap,
        handleDropdownChange,
        handleLanguageChange
    ]);
    // Handle global toggle
    const handleGlobalToggle = async (checked) => {
        const updatePromises = emailPreferences.map((pref) => {
            const updateRequest = {
                id: pref.id,
                emailType: pref.emailType,
                isEnabled: checked,
                language: pref.language,
                timingSettings: pref.timingSettings.map((ts) => ({
                    daysOfWeek: ts.daysOfWeek,
                    daysOfMonth: ts.daysOfMonth,
                    daysBefore: ts.daysBefore,
                    isDayItself: ts.isDayItself,
                    orderIndex: ts.orderIndex
                }))
            };
            return dispatch(updateEmailPreferenceAsync(updateRequest));
        });
        await Promise.all(updatePromises);
    };
    // Handle individual email type toggle
    const handleItemToggle = async (id, checked) => {
        const item = emailControlData.find((d) => d.id === id);
        if (!item)
            return;
        const existingPref = preferencesMap[item.emailType];
        if (existingPref) {
            // Update existing preference
            const updateRequest = {
                id: existingPref.id,
                emailType: existingPref.emailType,
                isEnabled: checked,
                language: existingPref.language,
                timingSettings: existingPref.timingSettings.map((ts) => ({
                    daysOfWeek: ts.daysOfWeek,
                    daysOfMonth: ts.daysOfMonth,
                    daysBefore: ts.daysBefore,
                    isDayItself: ts.isDayItself,
                    orderIndex: ts.orderIndex
                }))
            };
            await dispatch(updateEmailPreferenceAsync(updateRequest));
        }
        else {
            // Create new preference
            const timingValue = formValues?.[`dropdown-${id}`] || item.defaultOption;
            const languageValue = formValues?.[`language-${id}`] || String(Language.EN);
            let timingSettings = [];
            if (item.emailType === EmailType.SfeermasterActionList) {
                timingSettings = [
                    {
                        daysOfWeek: [Number(timingValue)],
                        orderIndex: 0
                    }
                ];
            }
            else if (item.emailType === EmailType.JubileesMonthlyOverview) {
                timingSettings = [
                    {
                        daysOfMonth: [Number(timingValue)],
                        orderIndex: 0
                    }
                ];
            }
            else if (item.emailType === EmailType.EventReminder) {
                const daysBeforeArray = Array.isArray(timingValue)
                    ? timingValue.map((v) => Number(v))
                    : [Number(timingValue)];
                timingSettings = [
                    {
                        daysBefore: daysBeforeArray,
                        isDayItself: false,
                        orderIndex: 0
                    }
                ];
            }
            const createRequest = {
                emailType: item.emailType,
                isEnabled: checked,
                language: Number(languageValue),
                timingSettings
            };
            await dispatch(createEmailPreferenceAsync(createRequest));
        }
    };
    return (_jsx(Box, { sx: { overflowY: 'auto' }, children: _jsx(Box, { sx: {
                overflowY: 'auto',
                height: isSmallScreen
                    ? isMobileScreen
                        ? 'calc(100dvh - 250px)'
                        : 'calc(100dvh - 300px)'
                    : 'calc(100dvh - 260px)'
            }, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: _jsxs(StyledAccordion, { expanded: expanded.includes('emailControl'), onChange: handleAccordionChange('emailControl'), children: [_jsx(StyledAccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flex: 1 }, children: [_jsx(Box, { sx: { color: theme.palette.primary.main }, children: _jsx(MagicStar, { size: 24, color: theme.palette.action.disabled, variant: 'Outline' }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(Typography, { variant: 'h6', sx: { fontWeight: 500 }, children: t('settings.emailControl.title') }) }), _jsx(FormControlLabel, { onClick: (e) => e.stopPropagation(), control: _jsx(Switch, { checked: emailControlEnabled, onChange: (e) => handleGlobalToggle(e.target.checked), disabled: emailPreferencesStatus === APIStatus.PENDING ||
                                                emailPreferences.length === 0 }), label: '', sx: { m: 0 } })] }) }), _jsx(StyledAccordionDetails, { children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: emailControlData.map((item) => (_jsxs(ItemContainer, { sx: {
                                        flexDirection: isSmallScreen ? 'column' : 'row',
                                        alignItems: isSmallScreen ? 'stretch' : 'center'
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1 }, children: [_jsx(Typography, { variant: 'body2', sx: { fontWeight: 400 }, children: item.label }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: item.description })] }), _jsxs(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                justifyContent: 'flex-end'
                                            }, children: [_jsx(Box, { sx: { minWidth: 150 }, children: _jsx(Controller, { name: `language-${item.id}`, control: control, render: ({ field }) => {
                                                            const selectedOption = languageOptions.find((opt) => opt.value === field.value) || undefined;
                                                            return (_jsx(Autocomplete, { ...field, options: languageOptions, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, value: selectedOption, onChange: (_, newValue) => {
                                                                    field.onChange(newValue?.value || String(Language.EN));
                                                                }, fullWidth: true, disableClearable: true, popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', color: theme.palette.grey[700] }), slotProps: {
                                                                    paper: {
                                                                        sx: {
                                                                            backgroundColor: '#2E3440',
                                                                            color: theme.palette.common.white,
                                                                            boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                                                                            '& .MuiAutocomplete-noOptions': {
                                                                                color: theme.palette.common.white
                                                                            }
                                                                        }
                                                                    }
                                                                }, renderOption: (props, option) => (_createElement("li", { ...props, key: option.value, style: { backgroundColor: 'transparent' } },
                                                                    _jsxs(Box, { sx: {
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            width: '100%'
                                                                        }, children: [_jsx(Box, { component: 'img', src: option.flag, alt: option.label, sx: { width: 20, height: 15, borderRadius: 0.5 } }), _jsx(Typography, { variant: 'body2', sx: { color: 'inherit', lineHeight: 1.5 }, children: option.label })] }))), renderInput: (params) => {
                                                                    const inputProps = { ...params.InputProps };
                                                                    if (selectedOption) {
                                                                        inputProps.startAdornment = (_jsx(Box, { component: 'img', src: selectedOption.flag, alt: selectedOption.label, sx: { width: 20, height: 15, borderRadius: 0.5, mr: 1 } }));
                                                                    }
                                                                    return (_jsx(HFilledTextField, { ...params, bg: 'light', label: t('common.selectLanguage'), sx: {
                                                                            border: 'none',
                                                                            '& .MuiInputBase-input': {
                                                                                opacity: selectedOption ? 0 : 1,
                                                                                width: selectedOption ? 0 : '100%'
                                                                            }
                                                                        }, slotProps: {
                                                                            input: {
                                                                                ...inputProps,
                                                                                disableUnderline: true
                                                                            }
                                                                        } }));
                                                                } }));
                                                        } }) }), _jsx(Box, { sx: { minWidth: 200 }, children: _jsx(SingleSelect, { control: control, name: `dropdown-${item.id}`, label: item.dropdownLabel, options: item.options, bg: 'light', multiple: item.isMultiple || false, disableCloseOnSelect: item.isMultiple || false }) }), _jsx(Switch, { checked: enabledStates[item.id] ?? false, onChange: (e) => handleItemToggle(item.id, e.target.checked), color: 'primary', disabled: emailPreferencesStatus === APIStatus.PENDING })] })] }, item.id))) }) })] }) }) }) }));
};
export default EmailControlTab;
